import { useEffect, useRef } from 'react'
import { APP_REGISTRY } from '@/registry/apps'
import type { AppDefinition } from '@/registry/types'
import { useModulePreferencesStore } from '@/shared/stores/modulePreferencesStore'
import { useModuleFlagsStore } from '@/shared/stores/moduleFlagsStore'
import { useAuth } from '@/shared/hooks/useAuth'

interface UseEnabledModulesResult {
  modules: AppDefinition[]
  isLoading: boolean
}

export function useEnabledModules(): UseEnabledModulesResult {
  const { user, loading: authLoading } = useAuth()
  const { preferences, loading, fetchPreferences, isModuleEnabled, reset } = useModulePreferencesStore()
  // Subscribe to the ids (not the checker fn) so flag changes re-render.
  const availableIds = useModuleFlagsStore((s) => s.availableIds)

  // Tracks the user id we've already kicked off a fetch for. A failed/blocked
  // insert leaves `preferences` null while the store's `loading` toggles
  // true→false; without this guard that toggle re-fired the effect and spun a
  // tight request storm. We attempt at most once per user; manual actions
  // (add/remove) still call fetchPreferences directly.
  const attemptedUserId = useRef<string | null>(null)

  // Determine if preferences are stale (belong to different user)
  const preferencesAreStale = preferences && user && preferences.user_id !== user.id
  const needsFetch = user && !loading && (!preferences || preferencesAreStale)
  const isLoading = authLoading || loading || needsFetch || preferencesAreStale

  useEffect(() => {
    if (!user) {
      if (preferences) {
        reset()
      }
      attemptedUserId.current = null
      return
    }

    // Already have this user's preferences — nothing to do.
    if (preferences && preferences.user_id === user.id) return

    // Don't auto-retry a fetch we've already attempted for this user. The store
    // coalesces concurrent callers internally, so we don't gate on `loading`
    // here (which is what re-fired the loop); the ref bounds retries instead.
    if (attemptedUserId.current === user.id) return
    attemptedUserId.current = user.id
    fetchPreferences()
  }, [user, preferences, fetchPreferences, reset])

  // Return empty array while loading to prevent flash
  if (isLoading) {
    return { modules: [], isLoading: true }
  }

  return {
    modules: APP_REGISTRY.filter(
      (app) =>
        (availableIds == null || availableIds.includes(app.id)) &&
        isModuleEnabled(app.id)
    ),
    isLoading: false
  }
}
